<template>
  <div :class="{ '-mx-5': showNewGallery && isMobile }">
    <ViewportPriority on>
      <div
        v-if="isMobile"
        :class="{
          'relative mx-auto w-[400px] max-w-[calc(70vh-220px)] [@media(max-width:400px)]:w-auto':
            showNewGallery,
          '-mx-5': !showNewGallery,
        }"
      >
        <template v-if="!showNewGallery">
          <BasicSlider
            ref="slider"
            :display-with-padding="false"
            class="w-auto"
            :items="items"
            key-prop="hash"
            :pixel-gap="0"
            :max-items-per-slide-mobile="2"
            @update:current-slide="currentSlideIndex = $event"
          >
            <template #item="{ item, index }">
              <div class="relative aspect-[7/10]">
                <ProductImage
                  v-if="index === 0"
                  :alt="productName"
                  :image="item"
                  sizes="sm:50vw md:50vw lg:50vw xl:50vw"
                  fit="cover"
                  is-pdp
                  @click="$emit('click:image', indexInProductImages(item))"
                />
                <UiVideo
                  v-else-if="item.videoUrl"
                  :src="item.videoUrl"
                  :placeholder-src="placeholderSrc"
                  autoplay
                  class="controls-hide-download size-full"
                  aspect-ratio="7/10"
                  loop
                />
                <ProductImage
                  v-else-if="item.hash"
                  :alt="`${productName} ${index + 1}`"
                  :image="item"
                  sizes="sm:50vw md:50vw lg:50vw xl:50vw"
                  fit="cover"
                  is-pdp
                  :low-priority="index > 1"
                  @click="$emit('click:image', indexInProductImages(item))"
                />
              </div>
            </template>
          </BasicSlider>
        </template>
        <template v-else>
          <Swiper
            :modules="[SwiperNavigation, SwiperPagination]"
            :slides-per-view="1"
            :space-between="0"
            :pagination="{
              el: '.swiper-pagination',
              clickable: true,
              type: 'fraction',
              renderFraction,
            }"
            :navigation="{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }"
            :loop="true"
            @swiper="swiper = $event"
            @slide-change="currentSlideIndex = $event.activeIndex"
          >
            <SwiperSlide
              v-for="(item, index) in items"
              :key="item.hash || index"
            >
              <div class="relative aspect-[7/10]">
                <ProductImage
                  v-if="index === 0"
                  :alt="productName"
                  :image="item"
                  sizes="(max-width: 400px):100vw (min-width: 401px):400px"
                  fit="cover"
                  is-pdp
                  @click="$emit('click:image', indexInProductImages(item))"
                />
                <UiVideo
                  v-else-if="item.videoUrl"
                  :src="item.videoUrl"
                  :placeholder-src="placeholderSrc"
                  autoplay
                  class="controls-hide-download size-full"
                  aspect-ratio="7/10"
                  loop
                />
                <ProductImage
                  v-else-if="item.hash"
                  :alt="`${productName} ${index + 1}`"
                  :image="item"
                  sizes="(max-width: 400px):100vw (min-width: 401px):400px"
                  fit="cover"
                  is-pdp
                  :low-priority="index > 1"
                  @click="$emit('click:image', indexInProductImages(item))"
                />
              </div>
            </SwiperSlide>

            <!-- Navigation arrows -->
            <button
              type="button"
              class="swiper-button-prev !left-0 !pl-2"
              :aria-label="$t('pdp.gallery.previous_image')"
            />
            <button
              type="button"
              class="swiper-button-next !right-0 !pr-2"
              :aria-label="$t('pdp.gallery.next_image')"
            />

            <!-- Pagination dots -->
            <div
              class="swiper-pagination absolute bottom-4 z-10 flex w-full justify-center gap-2"
            />
          </Swiper>
        </template>
      </div>
      <div v-else>
        <LazyTiledGallery
          :images="remainingImages"
          :product-order-number="productOrderNumber"
        >
          <template v-if="firstImage" #firstImage>
            <div
              class="w-full cursor-zoom-in"
              @click="$emit('click:image', indexInProductImages(firstImage))"
            >
              <ProductImage
                :image="firstImage"
                :alt="productName"
                sizes="sm:50vw md:30vw"
                fit="cover"
                is-pdp
                class="flex"
              />
            </div>
          </template>

          <template v-if="videoUrl" #video>
            <UiVideo
              :key="videoUrl"
              :src="videoUrl"
              :placeholder-src="placeholderSrc"
              autoplay
              aspect-ratio="7/10"
              class="controls-hide-download size-full"
              loop
            />
          </template>
          <template #body="{ image, idx }">
            <div
              class="w-full cursor-zoom-in"
              @click="$emit('click:image', indexInProductImages(image))"
            >
              <ProductImage
                :image="image"
                :alt="`${productName} ${idx + 1}`"
                sizes="sm:50vw md:30vw"
                fit="cover"
                is-pdp
                class="flex"
                :low-priority="idx > 2"
              />
            </div>
          </template>
        </LazyTiledGallery>
      </div>
    </ViewportPriority>
  </div>
</template>

<script lang="ts">
import type { ProductImage } from '@scayle/storefront-nuxt'
import { ref, computed } from 'vue'
import { defineComponent, type PropType } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination } from 'swiper/modules'
import { fetchLazy } from '#layers/base/utils/loading'
import useCms from '~/composables/cms/useCms'
import {
  filterOutRedundantImages,
  sortImagesByAssetType,
} from '#layers/base/utils/image'
import { useDeviceType } from '~/composables/useDeviceType'
import { useRuntimeConfig } from '#app/nuxt'
import type { SbProductConfig } from '~/cms/storyblok/types/storyblok.d.ts'
import 'swiper/css'
import 'swiper/css/pagination'
import useExperiments from '~/composables/useExperiments'

type ProductItem =
  | (ProductImage & { videoUrl?: never })
  | { hash: 'video'; videoUrl: string }

export default defineComponent({
  name: 'ProductImageGallery',
  components: {
    Swiper,
    SwiperSlide,
  },
  props: {
    images: {
      type: Array as PropType<Array<ProductImage>>,
      required: true,
    },
    productName: {
      type: String,
      required: true,
    },
    productOrderNumber: {
      type: String,
      default: '',
    },
    maxItemsPerSlideMobile: {
      type: Number,
      default: 2,
    },
  },
  emits: ['click:image'],

  async setup(props) {
    const config = useRuntimeConfig()
    const { isMobile } = useDeviceType()
    const { showNewGallery } = useExperiments()

    const sortedImages = computed(() =>
      sortImagesByAssetType(
        filterOutRedundantImages(props.images, isMobile.value),
        isMobile.value,
      ),
    )
    const firstImage = computed(() => sortedImages.value?.[0])
    const remainingImages = computed(() =>
      Array.isArray(sortedImages.value) ? sortedImages.value.slice(1) : [],
    )

    const videoRef = ref<HTMLVideoElement | null>(null)
    const { data, fetchByFolder } = useCms<SbProductConfig[]>(
      `video-${props.productOrderNumber}`,
    )

    await fetchLazy(
      fetchByFolder(
        `products/${config.public.appEnv !== 'production' ? 'prev-' : ''}${
          props.productOrderNumber
        }`,
      ),
    )

    const videoUrl = computed(
      () => data.value?.[0]?.content?.video?.filename || null,
    )

    const swiper = ref<any>(null)
    const currentSlideIndex = ref(0)
    const loadedSlides = ref<number[]>([0])

    return {
      firstImage,
      remainingImages,
      isMobile,
      videoRef,
      videoUrl,
      placeholderSrc: computed(
        () => data.value?.[0]?.content.video_placeholder?.filename,
      ),
      items: computed(() => {
        return [
          firstImage.value,
          videoUrl.value ? { hash: 'video', videoUrl: videoUrl.value } : null,
          ...remainingImages.value,
        ].filter(Boolean) as ProductItem[]
      }),
      renderFraction: (currentClass: string, totalClass: string) => {
        return `
          <div class="swiper-fraction-container bg-white/45 px-1 rounded-md">
            <span class="${currentClass}"></span> / <span class="${totalClass}"></span>
          </div>`
      },
      indexInProductImages: (image: ProductImage) => {
        const i = props.images.findIndex((img) => img.hash === image.hash)
        return i >= 0 ? i : 0
      },
      currentSlideIndex,
      loadedSlides,
      swiper,
      SwiperNavigation: Navigation,
      SwiperPagination: Pagination,
      showNewGallery,
    }
  },
})
</script>

<style>
.swiper-pagination-bullet {
  @apply size-2 rounded-full bg-black opacity-50;
}
.swiper-pagination-bullet-active {
  @apply opacity-100;
}

/* Navigation arrows styles */
.swiper-button-prev,
.swiper-button-next {
  @apply !h-10 !w-8 !text-black;
}
.swiper-button-prev::after,
.swiper-button-next::after {
  @apply !text-lg;
}
</style>
